import React, { useEffect, useState, useRef } from 'react';
import { X, Plus, Trash2, Edit2, Save, GripVertical, Check, ChevronDown, ChevronUp, Utensils, ImageIcon, DollarSign, Tag, FileText, Eye, EyeOff, ArrowLeft, Layers, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { supabaseClient } from '../lib/supabase';
import { useToast } from './ui/Toast';
interface MenuItemFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: any) => void;
  editingItem?: any;
  availableCategories?: string[];
}
interface SectionOption {
  id?: string;
  name: string;
  priceModifier: number;
  available: boolean;
  displayOrder: number;
}
interface MenuSection {
  id?: string;
  name: string;
  description: string;
  required: boolean;
  minSelections: number;
  maxSelections: number;
  displayOrder: number;
  options: SectionOption[];
}
export function MenuItemFormModal({
  isOpen,
  onClose,
  onSave,
  editingItem,
  availableCategories = []
}: MenuItemFormModalProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'sections'>('info');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: availableCategories[0] || '',
    price: '',
    image: '',
    catalogType: 'Almuerzo',
    available: true
  });
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null);
  // New states for templates
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const {
    showToast
  } = useToast();
  // Track previous isOpen to only reset on modal open
  const prevIsOpenRef = useRef(false);
  const editingItemIdRef = useRef<string | null>(null);
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (!isOpen) {
      editingItemIdRef.current = null;
      return;
    }
    // Skip if same item is already loaded (prevents polling/realtime resets)
    const currentItemId = editingItem?.id || null;
    if (!justOpened && currentItemId === editingItemIdRef.current) {
      return;
    }
    editingItemIdRef.current = currentItemId;
    if (editingItem) {
      setFormData({
        name: editingItem.name || '',
        description: editingItem.description || '',
        category: editingItem.category || availableCategories[0] || '',
        price: editingItem.price?.toString() || '',
        image: editingItem.image || '',
        catalogType: editingItem.catalogType || 'Almuerzo',
        available: editingItem.available !== false
      });
      if (editingItem.id) {
        loadSections(editingItem.id);
      }
    } else {
      setFormData({
        name: '',
        description: '',
        category: availableCategories[0] || '',
        price: '',
        image: '',
        catalogType: 'Almuerzo',
        available: true
      });
      setSections([]);
    }
    // Only reset tab to 'info' when the modal first opens
    if (justOpened) {
      setActiveTab('info');
    }
  }, [editingItem, isOpen]);
  const loadSections = async (menuItemId: string) => {
    setLoadingSections(true);
    try {
      const data = await supabaseClient.getMenuItemSections(menuItemId);
      setSections(data);
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setLoadingSections(false);
    }
  };
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const data = await supabaseClient.getSectionTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      showToast('error', 'Error', 'No se pudieron cargar las plantillas');
    } finally {
      setLoadingTemplates(false);
    }
  };
  const handleImportTemplate = async (templateId: string) => {
    if (!editingItem?.id) return;
    setIsImporting(true);
    try {
      const result = await supabaseClient.applyTemplateToItems(templateId, [editingItem.id]);
      if (result?.success) {
        if (result.applied_count > 0) {
          showToast('success', 'Plantilla importada', 'Las opciones se han agregado correctamente');
          await loadSections(editingItem.id);
        } else {
          showToast('info', 'Sin cambios', 'Esta plantilla ya estaba aplicada al plato');
        }
        setShowTemplateModal(false);
      } else {
        throw new Error('Error al aplicar plantilla');
      }
    } catch (error) {
      console.error('Error importing template:', error);
      showToast('error', 'Error', 'No se pudo importar la plantilla');
    } finally {
      setIsImporting(false);
    }
  };
  const handleAddSection = () => {
    const newSection: MenuSection = {
      name: '',
      description: '',
      required: false,
      minSelections: 0,
      maxSelections: 1,
      displayOrder: sections.length,
      options: []
    };
    setSections([...sections, newSection]);
    setEditingSectionIndex(sections.length);
    setExpandedSectionIndex(sections.length);
  };
  const handleSaveSection = async (index: number) => {
    if (!editingItem?.id) {
      showToast('warning', 'Guardar plato', 'Primero guarda el plato antes de agregar secciones');
      return;
    }
    const section = sections[index];
    if (!section.name.trim()) {
      showToast('warning', 'Nombre requerido', 'La sección debe tener un nombre');
      return;
    }
    try {
      if (section.id) {
        await supabaseClient.updateMenuSection(section.id, {
          name: section.name,
          description: section.description,
          required: section.required,
          minSelections: section.minSelections,
          maxSelections: section.maxSelections,
          displayOrder: section.displayOrder
        });
      } else {
        const newSection = await supabaseClient.createMenuSection({
          ...section,
          menuItemId: editingItem.id
        });
        const updatedSections = [...sections];
        updatedSections[index] = {
          ...section,
          id: newSection.id
        };
        setSections(updatedSections);
      }
      setEditingSectionIndex(null);
      showToast('success', 'Guardado', 'Sección guardada correctamente');
    } catch (error) {
      console.error('Error saving section:', error);
      showToast('error', 'Error', 'No se pudo guardar la sección');
    }
  };
  const handleDeleteSection = async (index: number) => {
    const section = sections[index];
    if (section.id) {
      if (!window.confirm('¿Eliminar esta sección y todas sus opciones?')) return;
      try {
        await supabaseClient.deleteMenuSection(section.id);
        showToast('success', 'Eliminado', 'Sección eliminada');
      } catch (error) {
        console.error('Error deleting section:', error);
        showToast('error', 'Error', 'No se pudo eliminar');
        return;
      }
    }
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
    if (editingSectionIndex === index) setEditingSectionIndex(null);
    if (expandedSectionIndex === index) setExpandedSectionIndex(null);
  };
  const handleAddOption = (sectionIndex: number) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].options.push({
      name: '',
      priceModifier: 0,
      available: true,
      displayOrder: updatedSections[sectionIndex].options.length
    });
    setSections(updatedSections);
  };
  const handleSaveOption = async (sectionIndex: number, optionIndex: number) => {
    const section = sections[sectionIndex];
    const option = section.options[optionIndex];
    if (!section.id) {
      showToast('warning', 'Guardar sección', 'Primero guarda la sección');
      return;
    }
    if (!option.name.trim()) {
      showToast('warning', 'Nombre requerido', 'La opción debe tener un nombre');
      return;
    }
    try {
      if (option.id) {
        await supabaseClient.updateSectionOption(option.id, {
          name: option.name,
          priceModifier: option.priceModifier,
          available: option.available,
          displayOrder: option.displayOrder
        });
      } else {
        const newOption = await supabaseClient.createSectionOption({
          ...option,
          sectionId: section.id
        });
        const updatedSections = [...sections];
        updatedSections[sectionIndex].options[optionIndex] = {
          ...option,
          id: newOption.id
        };
        setSections(updatedSections);
      }
      showToast('success', 'Guardado', 'Opción guardada');
    } catch (error) {
      console.error('Error saving option:', error);
      showToast('error', 'Error', 'No se pudo guardar');
    }
  };
  const handleDeleteOption = async (sectionIndex: number, optionIndex: number) => {
    const section = sections[sectionIndex];
    const option = section.options[optionIndex];
    if (option.id) {
      try {
        await supabaseClient.deleteSectionOption(option.id);
      } catch (error) {
        console.error('Error deleting option:', error);
        showToast('error', 'Error', 'No se pudo eliminar');
        return;
      }
    }
    const updatedSections = [...sections];
    updatedSections[sectionIndex].options = updatedSections[sectionIndex].options.filter((_, i) => i !== optionIndex);
    setSections(updatedSections);
  };
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
    onClose();
  };
  const inputClass = 'w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm transition-all placeholder:text-gray-400';
  const labelClass = 'block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2';
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-id="element-498">
      <div className="fixed inset-0 sm:relative sm:inset-auto w-full sm:max-w-4xl h-full sm:h-auto sm:max-h-[90vh] bg-white dark:bg-gray-900 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-499">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-100 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900 z-10" data-id="element-500">
          <div className="flex items-center gap-3" data-id="element-501">
            <button onClick={onClose} className="sm:hidden p-2 -ml-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full" data-id="element-502">
              <ArrowLeft size={20} data-id="element-503" />
            </button>
            <div className="flex items-center gap-3" data-id="element-504">
              {formData.image ? <div className="w-10 h-10 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shrink-0 hidden sm:block" data-id="element-505">
                  <img src={formData.image} alt="" className="w-full h-full object-cover" onError={e => {
                ;
                (e.target as HTMLImageElement).style.display = 'none';
              }} data-id="element-506" />
                </div> : <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 hidden sm:flex items-center justify-center shrink-0" data-id="element-507">
                  <Utensils size={18} className="text-gray-400" data-id="element-508" />
                </div>}
              <div data-id="element-509">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight" data-id="element-510">
                  {editingItem ? 'Editar Plato' : 'Nuevo Plato'}
                </h2>
                {editingItem && <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block" data-id="element-511">
                    {editingItem.name}
                  </p>}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="hidden sm:flex p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-512">
            <X size={20} data-id="element-513" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 shrink-0" data-id="element-514">
          <button onClick={() => setActiveTab('info')} className={`flex-1 sm:flex-none relative px-6 py-3 text-sm font-semibold transition-colors text-center ${activeTab === 'info' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-515">
            Información
            {activeTab === 'info' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 dark:bg-white" data-id="element-516" />}
          </button>
          <button onClick={() => setActiveTab('sections')} className={`flex-1 sm:flex-none relative px-6 py-3 text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${activeTab === 'sections' ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-517">
            Opciones
            {sections.length > 0 && <span className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded-md text-[10px] font-bold" data-id="element-518">
                {sections.length}
              </span>}
            {activeTab === 'sections' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-900 dark:bg-white" data-id="element-519" />}
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900" data-id="element-520">
          {activeTab === 'info' ? <form id="menu-item-form" onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6 sm:space-y-8 pb-24 sm:pb-8" data-id="element-521">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-id="element-522">
                <div className="space-y-1" data-id="element-523">
                  <label className={labelClass} data-id="element-524">Nombre del plato</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className={inputClass} placeholder="Ej: Pollo al Horno" data-id="element-525" />
                </div>
                <div className="space-y-1" data-id="element-526">
                  <label className={labelClass} data-id="element-527">Precio (RD$)</label>
                  <div className="relative" data-id="element-528">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-529">
                      <DollarSign size={16} className="text-gray-400" data-id="element-530" />
                    </div>
                    <input type="number" required step="0.01" value={formData.price} onChange={e => setFormData({
                  ...formData,
                  price: e.target.value
                })} className={`${inputClass} pl-10 font-mono font-bold text-base`} placeholder="0.00" data-id="element-531" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-1" data-id="element-532">
                <label className={labelClass} data-id="element-533">Descripción</label>
                <textarea required rows={3} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} className={`${inputClass} resize-none`} placeholder="Describe los ingredientes y preparación..." data-id="element-534" />
              </div>

              {/* Image URL */}
              <div className="space-y-1" data-id="element-535">
                <label className={labelClass} data-id="element-536">Imagen del plato (URL)</label>
                <div className="flex gap-3 items-start" data-id="element-537">
                  <div className="relative flex-1" data-id="element-538">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-539">
                      <ImageIcon size={16} className="text-gray-400" data-id="element-540" />
                    </div>
                    <input type="url" value={formData.image} onChange={e => setFormData({
                  ...formData,
                  image: e.target.value
                })} className={`${inputClass} pl-10`} placeholder="https://ejemplo.com/imagen.jpg" data-id="element-541" />
                  </div>
                  {formData.image && <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shrink-0 bg-gray-100 dark:bg-gray-800" data-id="element-542">
                      <img src={formData.image} alt="Preview" className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).style.display = 'none';
                }} data-id="element-543" />
                    </div>}
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-id="element-544">
                <div className="space-y-1" data-id="element-545">
                  <label className={labelClass} data-id="element-546">Categoría</label>
                  <div className="relative" data-id="element-547">
                    <select value={formData.category} onChange={e => setFormData({
                  ...formData,
                  category: e.target.value
                })} className={`${inputClass} appearance-none cursor-pointer`} data-id="element-548">
                      {availableCategories.map(category => <option key={category} value={category} data-id="element-549">
                          {category}
                        </option>)}
                    </select>
                    <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-550" />
                  </div>
                </div>
                <div className="space-y-1" data-id="element-551">
                  <label className={labelClass} data-id="element-552">Tipo de menú</label>
                  <div className="relative" data-id="element-553">
                    <select value={formData.catalogType} onChange={e => setFormData({
                  ...formData,
                  catalogType: e.target.value
                })} className={`${inputClass} appearance-none cursor-pointer`} data-id="element-554">
                      <option value="Almuerzo" data-id="element-555">Almuerzo</option>
                      <option value="Cena" data-id="element-556">Cena</option>
                    </select>
                    <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-557" />
                  </div>
                </div>
                <div className="space-y-1" data-id="element-558">
                  <label className={labelClass} data-id="element-559">Estado</label>
                  <button type="button" onClick={() => setFormData({
                ...formData,
                available: !formData.available
              })} className={`w-full px-4 py-3 rounded-xl border text-sm font-semibold transition-all flex items-center justify-between ${formData.available ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'}`} data-id="element-560">
                    <span className="flex items-center gap-2" data-id="element-561">
                      {formData.available ? <Eye size={16} data-id="element-562" /> : <EyeOff size={16} data-id="element-563" />}
                      {formData.available ? 'Visible' : 'Oculto'}
                    </span>
                    <div className={`w-9 h-5 rounded-full relative transition-colors ${formData.available ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-564">
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${formData.available ? 'right-0.5' : 'left-0.5'}`} data-id="element-565" />
                    </div>
                  </button>
                </div>
              </div>
            </form> : <div className="p-4 sm:p-8 pb-24 sm:pb-8" data-id="element-566">
              {!editingItem?.id ? <div className="flex flex-col items-center justify-center py-16 px-4 text-center bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-567">
                  <div className="w-16 h-16 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-sm mb-4" data-id="element-568">
                    <FileText size={32} className="text-gray-300 dark:text-gray-600" data-id="element-569" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1" data-id="element-570">
                    Guarda el plato primero
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mb-6" data-id="element-571">
                    Para agregar opciones y extras, primero debes guardar la
                    información básica del plato.
                  </p>
                  <Button size="sm" onClick={() => setActiveTab('info')} data-id="element-572">
                    Ir a Información
                  </Button>
                </div> : loadingSections ? <div className="flex justify-center py-20" data-id="element-573">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" data-id="element-574"></div>
                </div> : <div className="space-y-6" data-id="element-575">
                  {sections.map((section, index) => <div key={section.id || index} className="bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm" data-id="element-576">
                      {/* Section Header */}
                      <div className="p-4 sm:p-5 flex items-start gap-4" data-id="element-577">
                        <button onClick={() => setExpandedSectionIndex(expandedSectionIndex === index ? null : index)} className="mt-1 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-578">
                          {expandedSectionIndex === index ? <ChevronUp size={20} data-id="element-579" /> : <ChevronDown size={20} data-id="element-580" />}
                        </button>

                        <div className="flex-1 min-w-0" data-id="element-581">
                          {editingSectionIndex === index ? <div className="space-y-4" data-id="element-582">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-583">
                                <input type="text" value={section.name} onChange={e => {
                        const updated = [...sections];
                        updated[index].name = e.target.value;
                        setSections(updated);
                      }} placeholder="Nombre (ej: Elige proteína)" className={inputClass} autoFocus data-id="element-584" />
                                <input type="text" value={section.description} onChange={e => {
                        const updated = [...sections];
                        updated[index].description = e.target.value;
                        setSections(updated);
                      }} placeholder="Descripción (opcional)" className={inputClass} data-id="element-585" />
                              </div>

                              <div className="flex flex-wrap items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-586">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer select-none" data-id="element-587">
                                  <input type="checkbox" checked={section.required} onChange={e => {
                          const updated = [...sections];
                          updated[index].required = e.target.checked;
                          setSections(updated);
                        }} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-588" />
                                  Obligatorio
                                </label>
                                <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-2 hidden sm:block" data-id="element-589"></div>
                                <div className="flex items-center gap-2" data-id="element-590">
                                  <span className="text-xs text-gray-500 font-bold uppercase" data-id="element-591">
                                    Mín
                                  </span>
                                  <input type="number" min="0" value={section.minSelections} onChange={e => {
                          const updated = [...sections];
                          updated[index].minSelections = parseInt(e.target.value) || 0;
                          setSections(updated);
                        }} className="w-16 px-2 py-1 text-center text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" data-id="element-592" />
                                </div>
                                <div className="flex items-center gap-2" data-id="element-593">
                                  <span className="text-xs text-gray-500 font-bold uppercase" data-id="element-594">
                                    Máx
                                  </span>
                                  <input type="number" min="1" value={section.maxSelections} onChange={e => {
                          const updated = [...sections];
                          updated[index].maxSelections = parseInt(e.target.value) || 1;
                          setSections(updated);
                        }} className="w-16 px-2 py-1 text-center text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" data-id="element-595" />
                                </div>
                              </div>

                              <div className="flex gap-2 pt-2" data-id="element-596">
                                <Button size="sm" onClick={() => handleSaveSection(index)} leftIcon={<Save size={14} data-id="element-598" />} data-id="element-597">
                                  Guardar Sección
                                </Button>
                              </div>
                            </div> : <div onClick={() => setExpandedSectionIndex(expandedSectionIndex === index ? null : index)} className="cursor-pointer" data-id="element-599">
                              <h3 className="font-bold text-gray-900 dark:text-white text-base" data-id="element-600">
                                {section.name || 'Nueva Sección'}
                              </h3>
                              <div className="flex flex-wrap gap-2 mt-1.5" data-id="element-601">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${section.required ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-602">
                                  {section.required ? 'Obligatorio' : 'Opcional'}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-603">
                                  {section.minSelections} -{' '}
                                  {section.maxSelections} opciones
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" data-id="element-604">
                                  {section.options.length} ítems
                                </span>
                              </div>
                            </div>}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-1" data-id="element-605">
                          {editingSectionIndex !== index && <button onClick={() => setEditingSectionIndex(index)} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-606">
                              <Edit2 size={18} data-id="element-607" />
                            </button>}
                          <button onClick={() => handleDeleteSection(index)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-608">
                            <Trash2 size={18} data-id="element-609" />
                          </button>
                        </div>
                      </div>

                      {/* Options List */}
                      {expandedSectionIndex === index && <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4 sm:p-5" data-id="element-610">
                          <div className="space-y-3 mb-4" data-id="element-611">
                            {section.options.map((option, optIndex) => <div key={option.id || optIndex} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-612">
                                <div className="flex items-center gap-3 flex-1" data-id="element-613">
                                  <GripVertical size={16} className="text-gray-300 dark:text-gray-600 cursor-move shrink-0" data-id="element-614" />
                                  <input type="text" value={option.name} onChange={e => {
                        const updated = [...sections];
                        updated[index].options[optIndex].name = e.target.value;
                        setSections(updated);
                      }} placeholder="Nombre opción" className="flex-1 min-w-0 bg-transparent border-none p-0 text-sm font-medium text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0" data-id="element-615" />
                                </div>

                                <div className="flex items-center justify-between sm:justify-end gap-3 pl-7 sm:pl-0" data-id="element-616">
                                  <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 rounded-lg px-2 py-1 border border-gray-100 dark:border-gray-700" data-id="element-617">
                                    <span className="text-xs text-gray-400 font-bold" data-id="element-618">
                                      +RD$
                                    </span>
                                    <input type="number" step="0.01" value={option.priceModifier} onChange={e => {
                          const updated = [...sections];
                          updated[index].options[optIndex].priceModifier = parseFloat(e.target.value) || 0;
                          setSections(updated);
                        }} className="w-16 bg-transparent border-none p-0 text-sm font-mono text-right focus:ring-0" placeholder="0" data-id="element-619" />
                                  </div>

                                  <div className="flex items-center gap-1" data-id="element-620">
                                    <button onClick={() => {
                          const updated = [...sections];
                          updated[index].options[optIndex].available = !option.available;
                          setSections(updated);
                        }} className={`p-1.5 rounded-lg transition-colors ${option.available ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'text-gray-400 bg-gray-100 dark:bg-gray-700'}`} title={option.available ? 'Visible' : 'Oculto'} data-id="element-621">
                                      {option.available ? <Eye size={16} data-id="element-622" /> : <EyeOff size={16} data-id="element-623" />}
                                    </button>

                                    <button onClick={() => handleSaveOption(index, optIndex)} className="p-1.5 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-624">
                                      <Save size={16} data-id="element-625" />
                                    </button>

                                    <button onClick={() => handleDeleteOption(index, optIndex)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-626">
                                      <Trash2 size={16} data-id="element-627" />
                                    </button>
                                  </div>
                                </div>
                              </div>)}

                            {section.options.length === 0 && <div className="text-center py-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl" data-id="element-628">
                                <p className="text-sm text-gray-400" data-id="element-629">
                                  No hay opciones en esta sección
                                </p>
                              </div>}
                          </div>

                          <Button size="sm" variant="outline" onClick={() => handleAddOption(index)} leftIcon={<Plus size={16} data-id="element-631" />} fullWidth data-id="element-630">
                            Agregar Opción
                          </Button>
                        </div>}
                    </div>)}

                  {sections.length === 0 && <div className="text-center py-12 px-4" data-id="element-632">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-633">
                        <Tag size={32} className="text-gray-400" data-id="element-634" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1" data-id="element-635">
                        Sin secciones
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs mx-auto mb-6" data-id="element-636">
                        Agrega secciones para permitir que los clientes
                        personalicen su pedido (ej: Guarniciones, Bebidas).
                      </p>
                    </div>}

                  <div className="flex flex-col sm:flex-row gap-3 pt-2" data-id="element-637">
                    <Button variant="outline" onClick={() => {
                loadTemplates();
                setShowTemplateModal(true);
              }} leftIcon={<Layers size={18} data-id="element-639" />} className="flex-1 py-4 border-dashed border-2 hover:border-gray-400 dark:hover:border-gray-500" data-id="element-638">
                      Importar Plantilla
                    </Button>
                    <Button onClick={handleAddSection} leftIcon={<Plus size={18} data-id="element-641" />} className="flex-1 py-4 shadow-lg shadow-gray-900/5" data-id="element-640">
                      Crear Nueva Sección
                    </Button>
                  </div>
                </div>}
            </div>}
        </div>

        {/* Fixed Footer for Info Tab - Always visible on mobile */}
        {activeTab === 'info' && <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0 z-20" data-id="element-642">
            <div className="flex gap-3" data-id="element-643">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1" data-id="element-644">
                Cancelar
              </Button>
              <Button onClick={handleSubmit} // Trigger form submit from outside
          className="flex-[2]" data-id="element-645">
                {editingItem ? 'Guardar Cambios' : 'Crear Plato'}
              </Button>
            </div>
          </div>}
      </div>

      {/* Template Selection Modal */}
      {showTemplateModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-id="element-646">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800 overflow-hidden flex flex-col max-h-[80vh]" data-id="element-647">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/30" data-id="element-648">
              <div data-id="element-649">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-650">
                  <Layers size={20} className="text-gray-400" data-id="element-651" />
                  Importar Plantilla
                </h3>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-652">
                  Selecciona un grupo de opciones predefinido
                </p>
              </div>
              <button onClick={() => setShowTemplateModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" disabled={isImporting} data-id="element-653">
                <X size={20} data-id="element-654" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1" data-id="element-655">
              {loadingTemplates ? <div className="flex justify-center py-10" data-id="element-656">
                  <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-657" />
                </div> : templates.length === 0 ? <div className="text-center py-10" data-id="element-658">
                  <Layers size={32} className="text-gray-300 mx-auto mb-3" data-id="element-659" />
                  <p className="text-gray-500 font-medium" data-id="element-660">
                    No hay plantillas creadas
                  </p>
                  <p className="text-xs text-gray-400 mt-1" data-id="element-661">
                    Crea plantillas desde la vista principal del menú
                  </p>
                </div> : <div className="space-y-2" data-id="element-662">
                  {templates.map(template => <button key={template.id} onClick={() => handleImportTemplate(template.id)} disabled={isImporting} className="w-full text-left p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-gray-900 dark:hover:border-white hover:shadow-md transition-all bg-white dark:bg-gray-800 group disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-663">
                      <div className="flex justify-between items-start mb-1" data-id="element-664">
                        <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" data-id="element-665">
                          {template.name}
                        </h4>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${template.required ? 'bg-red-50 text-red-600 dark:bg-red-900/20' : 'bg-gray-100 text-gray-600 dark:bg-gray-700'}`} data-id="element-666">
                          {template.required ? 'Obligatorio' : 'Opcional'}
                        </span>
                      </div>
                      {template.description && <p className="text-xs text-gray-500 mb-2" data-id="element-667">
                          {template.description}
                        </p>}
                      <div className="flex items-center gap-3 text-[11px] text-gray-500 font-medium" data-id="element-668">
                        <span className="flex items-center gap-1" data-id="element-669">
                          <Check size={12} data-id="element-670" />
                          {template.minSelections} a {template.maxSelections}{' '}
                          opciones
                        </span>
                        <span className="flex items-center gap-1" data-id="element-671">
                          <Tag size={12} data-id="element-672" />
                          {template.options?.length || 0} ítems
                        </span>
                      </div>
                    </button>)}
                </div>}
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30" data-id="element-673">
              <Button variant="outline" fullWidth onClick={() => setShowTemplateModal(false)} disabled={isImporting} data-id="element-674">
                Cancelar
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}